import React, { useState, type ReactNode, forwardRef } from 'react';
import { useQueryLoader } from 'react-relay';
import type { ButtonProps, CustomThemeButtonProps } from '@atlaskit/button';
import { fg } from '@atlassian/jira-feature-gating';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import StartSprintQuery, {
	type mainJiraSoftwareStartSprintPrototypeQuery as StartSprintModalQueryType,
} from '@atlassian/jira-relay/src/__generated__/mainJiraSoftwareStartSprintPrototypeQuery.graphql';
import { ModalTriggerButton } from '@atlassian/jira-software-modal-dialog/src/ui/modal-trigger-button/index.tsx';
import { BACKLOG } from '@atlassian/jira-software-resource-invalidator/src/common/types.tsx';
import { triggerResourceCacheInvalidation } from '@atlassian/jira-software-resource-invalidator/src/controllers/resources-cache-invalidation-subject/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useBoardUrl } from '../../../common/hooks/use-board-url/index.tsx';
import { startSprintModalLoad } from '../../../services/utils/performance-analytics/index.tsx';
import { showSprintStartedFlagAction } from '../../../state/actions/sprints/start/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import {
	getHasActiveSprints,
	getRapidViewId,
	getIsCompanyManaged,
} from '../../../state/selectors/app/index.tsx';
import { DisabledButtonHelper } from '../common/sprint-actions/disabled-button-helper/index.tsx';
import StartSprintModal from './modal/index.tsx';

export type Props = {
	isDisabled: boolean;
	theme?: CustomThemeButtonProps['theme'];
	appearance?: ButtonProps['appearance'];
	sprintId: string;
	testId: string;
	children: ReactNode;
	redirectTo: string;
	shouldShowManageSprintHelper?: boolean;
	onSave: () => void;
};

const StartSprintButton = forwardRef<HTMLButtonElement | null, Props>(
	(
		{
			theme,
			appearance,
			isDisabled,
			testId,
			children,
			sprintId,
			shouldShowManageSprintHelper = false,
		}: Props,
		ref,
	) => {
		const boardId = useBacklogSelector(getRapidViewId);
		const hasActiveSprints = useBacklogSelector(getHasActiveSprints);
		const dispatch = useBacklogDispatch();
		const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

		const onSave = () => {
			// Since the sprint modals don't go through redux we need to fire their invalidations manually
			triggerResourceCacheInvalidation(BACKLOG, 'START_SPRINT_MODAL_START');
			dispatch(showSprintStartedFlagAction());
		};

		const redirectTo =
			useBoardUrl(
				hasActiveSprints ? ['sprintStarted=true', `sprints=${sprintId}`] : ['sprintStarted=true'],
			) || '';

		const [isModalVisible, setModalVisible] = useState(false);
		const cloudId = useCloudId();
		const [queryReference, loadQuery, disposeQuery] =
			useQueryLoader<StartSprintModalQueryType>(StartSprintQuery);

		const boardIdAri = createAri({
			resourceOwner: 'jira-software',
			cloudId,
			resourceType: 'board',
			resourceId: String(boardId),
		});

		const sprintIdAri = createAri({
			resourceOwner: 'jira-software',
			cloudId,
			resourceType: 'sprint',
			resourceId: String(sprintId),
		});

		const onClick = () => {
			startSprintModalLoad.start();
			loadQuery(
				{
					boardId: boardIdAri,
					sprintId: sprintIdAri,
					isCompanyManaged,
				},
				{
					fetchPolicy: 'network-only',
				},
			);

			setModalVisible(true);
		};

		const onClose = () => {
			setModalVisible(false);
			disposeQuery();
		};

		const onStartSprintSave = () => {
			onClose();
			onSave();
		};

		const onReady = () => {
			startSprintModalLoad.stop({
				customData: {
					relay: true,
				},
			});
		};

		const commonProps = {
			onClick,
			isDisabled,
			testId,
			ref,
		};

		const buttonComponent = theme ? (
			<ModalTriggerButton
				{...commonProps}
				theme={theme}
				spacing={isVisualRefreshEnabled() ? 'compact' : undefined}
			>
				{children}
			</ModalTriggerButton>
		) : (
			<ModalTriggerButton
				{...commonProps}
				appearance={isVisualRefreshEnabled() ? undefined : appearance}
				spacing={isVisualRefreshEnabled() ? 'compact' : undefined}
			>
				{children}
			</ModalTriggerButton>
		);

		return (
			<>
				{shouldShowManageSprintHelper && fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions') ? (
					<DisabledButtonHelper>{children}</DisabledButtonHelper>
				) : (
					buttonComponent
				)}
				{isModalVisible && queryReference ? (
					<StartSprintModal
						analyticsSource="software-backlog"
						onClose={onClose}
						onSave={onStartSprintSave}
						queryReference={queryReference}
						onReady={onReady}
						sprintId={String(sprintIdAri)}
						boardId={String(boardIdAri)}
						redirectTo={redirectTo}
						showAlternateHeaderMessage={hasActiveSprints}
						isCompanyManaged={isCompanyManaged}
					/>
				) : null}
			</>
		);
	},
);

export default StartSprintButton;
